import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, Paper } from '@mui/material';
import * as yup from 'yup';

import {
  DualButton,
  HorizontalStepper,
  ImagePreviewCard,
  RhfTextField,
  AutocompleteDropdown
} from '@components';
import { colors } from '@constants/colors';
import { translate } from '@utils/locale';
import { EmptySearchState } from '@assets/icons';
import {
  IMAGE_FORMATS,
  LONG_FIELD_MAX_LENGTH,
  TEXT_FIELD_MAX_LENGTH
} from '@constants/common';
import { generateFileUrl, getFileNameFromUrl } from '@utils/common';
import { ToastTypes } from '@type/toast';
import { OptionsLOVType } from '@components/auto-complete/types';
import { showGenericErrorToast } from '@utils/error';
import displayToast from '@components/toast/customToast';
import ImageUploader from '@containers/ImageUploader/ImageUploader';

import {
  AddAngelInvestorModalPropsType,
  CreateAngelInvestorFormFieldValues,
  ExternalInvestorType,
  UpdateInvestorRes
} from '../types';
import { FIELD_TO_TAB_MAPPER_ANGEL_INVESTOR } from '../constants';
import {
  useGetInvestorQuery,
  useCreateInvestorMutation,
  useUpdateInvestorMutation
} from '../api';
import { formatCreateUpdateAngelInvestorPayload } from '../services/investors';

const AddAngelInvestorModal: FC<AddAngelInvestorModalPropsType> = ({
  handleCloseModal,
  isOpen,
  setModalOpen,
  setIsDeleteModalOpen,
  memberDetails,
  roundId,
  disableChooseInvestor,
  resetAngelOffset,
  maxOrder
}) => {
  const steps = ['Member Info', 'Upload Photos'];

  const { companyId } = useParams();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [deleteClickedFile, setDeleteClickedFile] = useState<string>('');

  const [createInvestor, { isLoading }] = useCreateInvestorMutation();
  const [updateInvestor, { isLoading: isInvestorUpdating }] =
    useUpdateInvestorMutation();
  const { data: investorsList } = useGetInvestorQuery({
    companyId: companyId ?? '',
    payload: { roundId }
  });

  const invertorsUserIds = investorsList?.data?.map(val => val.user_id);

  const { data: wholeInvestorsList } = useGetInvestorQuery({
    payload: {}
  });

  const companyInvestorList = wholeInvestorsList?.data
    ?.filter(val => val.type === ExternalInvestorType.SuperAngelInvestor)
    .filter(val => !invertorsUserIds?.includes(val.user_id));
  const companyInvestorNamesList =
    companyInvestorList?.map(val => ({ id: val.id ?? '', name: val.name })) ??
    [];

  const schema = yup.object().shape({
    angelInvestorId: disableChooseInvestor
      ? yup.string()
      : yup.string().required(translate('requiredInput')),
    angelInvestorName: yup
      .string()
      .required(translate('company.placeholders.angelName'))
      .test(
        'required',
        translate('company.placeholders.angelName'),
        val => !!val.trimEnd()
      ),
    angelInvestorURL: yup
      .string()
      .url(translate('company.placeholders.angelUrl'))
      .nullable(),
    angelInvestorDesignation: yup.string().nullable(),
    angelInvestorDescription: yup
      .string()
      .required(translate('company.placeholders.angelInvestorDescription'))
      .test(
        'required',
        translate('company.placeholders.angelInvestorDescription'),
        val => !!val.trimEnd()
      ),
    angelInvestorLogo: yup
      .mixed()
      .test(
        translate('requiredInput'),
        translate('company.placeholders.angelImage'),
        (value: { file: File }) => {
          return !!value?.file;
        }
      )
  });

  const {
    control,
    reset,
    trigger,
    getValues,
    setValue,
    clearErrors,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm<CreateAngelInvestorFormFieldValues>({
    resolver: yupResolver(
      schema
    ) as never as Resolver<CreateAngelInvestorFormFieldValues>,
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  const onSubmit = async (data: CreateAngelInvestorFormFieldValues) => {
    try {
      if (memberDetails?.id) {
        const response = await updateInvestor({
          companyId: companyId ?? '',
          payload: formatCreateUpdateAngelInvestorPayload(data, roundId),
          investorId: memberDetails.id
        }).unwrap();
        handleCreateUpdateRes(response);
      } else {
        const response = await createInvestor({
          companyId: companyId ?? '',
          payload: formatCreateUpdateAngelInvestorPayload(
            { ...data, angelInvestorOrder: maxOrder + 1 },
            roundId
          )
        }).unwrap();
        handleCreateUpdateRes(response);
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  const typedInvestorName = useWatch({
    control: control,
    name: 'angelInvestorName'
  });

  const typedInvesterExists = companyInvestorNamesList?.find(
    val => val.name === typedInvestorName
  );

  const showAngelInvestorLogoFileUploader =
    deleteClickedFile === 'angelInvestorLogo' ||
    !getValues('angelInvestorLogo.path');

  const getImageUrl = () => {
    return generateFileUrl(
      getValues('angelInvestorLogo.path'),
      getValues('angelInvestorLogo.file')
    );
  };

  const getFileName = () => getValues('angelInvestorLogo.file')?.name || '';

  const deleteFile = (value: string) => {
    setDeleteClickedFile(value);
    setValue(value as never as keyof CreateAngelInvestorFormFieldValues, {
      file: undefined,
      path: ''
    });
  };

  const onFileUploadSuccess = (file: File, path: string) => {
    setValue('angelInvestorLogo', {
      file: file,
      path: path
    });
    clearErrors('angelInvestorLogo');
    setDeleteClickedFile('');
  };

  const onClickCancel = () => {
    reset({});
    handleCloseModal();
  };

  const onNextClick = async (currentStep: number) => {
    const isValid = await trigger(
      FIELD_TO_TAB_MAPPER_ANGEL_INVESTOR[
        currentStep as keyof typeof FIELD_TO_TAB_MAPPER_ANGEL_INVESTOR
      ] as never as keyof CreateAngelInvestorFormFieldValues
    );

    if (isValid) {
      if (currentStep === -1) {
        const selectedMemberDetails = companyInvestorList?.find(
          item => item.id === getValues('angelInvestorId')
        );
        if (selectedMemberDetails)
          onSubmit({
            angelInvestorName: selectedMemberDetails.name,
            angelInvestorURL: selectedMemberDetails.url,
            angelInvestorDesignation: selectedMemberDetails.designation,
            angelInvestorLogo: {
              file: selectedMemberDetails.logo_path
                ? new File(
                    [],
                    getFileNameFromUrl(selectedMemberDetails.logo_path)
                  )
                : undefined,
              path: selectedMemberDetails.logo_path
            },
            angelInvestorDescription: selectedMemberDetails?.description || '',
            angelInvestorId: selectedMemberDetails.id,
            angelInvestorUserId: selectedMemberDetails.user_id ?? '',
            angelInvestorVisibleInDeal:
              selectedMemberDetails.is_visible_in_deal ?? false,
            angelInvestorCreatedAt: selectedMemberDetails.created_at ?? '',
            angelInvestorOrder: selectedMemberDetails.order ?? 0
          });
        else
          setError('angelInvestorId', {
            message: 'Required',
            type: 'required'
          });
        return;
      }
      if (currentStep < steps.length - 1) setActiveStep(currentStep + 1);
      else onSubmit(getValues());
    } else {
      setActiveStep(currentStep);
    }
  };

  useHotkeys('enter', () => onNextClick(activeStep), { enabled: isOpen });

  const handleStepClick = async (index: number) => {
    const prevStep = activeStep;
    const newStep = index;
    if (prevStep > newStep) setActiveStep(newStep);
    else {
      const isValid = await trigger(
        FIELD_TO_TAB_MAPPER_ANGEL_INVESTOR[
          prevStep as keyof typeof FIELD_TO_TAB_MAPPER_ANGEL_INVESTOR
        ] as never as keyof CreateAngelInvestorFormFieldValues
      );
      if (!isValid || prevStep === newStep) return;
      setActiveStep(newStep);
    }
  };

  const handleCreateUpdateRes = (response: UpdateInvestorRes) => {
    if (response.status === 'ok') {
      setModalOpen(false);
      reset({});
      resetAngelOffset();
      handleCloseModal();
    } else {
      displayToast(
        ToastTypes.ERROR,
        response.error?.details?.[0] ?? 'Failed to Add Angel Investor'
      );
    }
  };

  const onClickPrimaryButton = () => {
    onNextClick(activeStep);
  };

  const onClickSecondaryButton = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
    else onClickCancel();
  };

  const AddInvestorButton = () => {
    setValue('angelInvestorId', ' ');
    setActiveStep(0);
  };

  const emptyState =
    typedInvestorName && !typedInvesterExists ? (
      <div className="flex flex-col gap-2 justify-center w-full h-[300px]">
        <div className="flex justify-center">
          <EmptySearchState />
        </div>
        <div className="text-center">
          {translate('company.investorsTable.emptyState', {
            searchTerm: typedInvestorName
          })}
        </div>
        <div className="flex gap-4 justify-center">
          <Button variant="contained" onClick={AddInvestorButton}>
            {translate('company.investorsTable.createAngelInvestor')}
          </Button>
        </div>
      </div>
    ) : (
      <></>
    );

  const handleInvestorNameChange = (_: ChangeEvent<{}>, value: string) => {
    clearErrors('angelInvestorName');
    setValue('angelInvestorName', value);
    clearErrors('angelInvestorId');
  };

  const onChangeFieldValue = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: keyof CreateAngelInvestorFormFieldValues
  ) => {
    setValue(name, event.target.value);
    clearErrors(name);
  };

  const onInvestorSelect = (selectedItem: OptionsLOVType) => {
    clearErrors('angelInvestorId');
    setValue('angelInvestorId', selectedItem?.id ?? '');
  };

  const renderForm = () => {
    switch (activeStep) {
      case -1:
        return (
          <>
            <AutocompleteDropdown
              control={control}
              name={'angelInvestorId'}
              placeholder={translate('company.addAngelInvestor.name')}
              options={companyInvestorNamesList}
              errors={errors}
              wrapperClass="pt-6 mr-6 w-full"
              handleInputChange={handleInvestorNameChange}
              onChangeHandler={onInvestorSelect}
              selectedItem={companyInvestorNamesList.find(
                value => value.id === (getValues('angelInvestorId') ?? '')
              )}
              inputValue={typedInvestorName}
              clearOnBlur={false}
              maxLength={TEXT_FIELD_MAX_LENGTH}
              autoFocusField={true}
              showCharacterLimit
            />
            {emptyState}
          </>
        );
      case 0:
        return (
          <>
            <RhfTextField
              name="angelInvestorName"
              label={translate('company.addAngelInvestor.name')}
              placeholder={translate('company.placeholders.angelName')}
              control={control}
              errors={errors}
              wrapperClass="mb-5 w-full"
              onChange={event => onChangeFieldValue(event, 'angelInvestorName')}
              maxLength={TEXT_FIELD_MAX_LENGTH}
              showCharacterLimit
              autoFocusField={true}
            />
            <RhfTextField
              name="angelInvestorDesignation"
              label={translate('company.addAngelInvestor.designation')}
              placeholder={translate('company.placeholders.angelDesignation')}
              control={control}
              errors={errors}
              wrapperClass="mb-5 w-full"
              maxLength={TEXT_FIELD_MAX_LENGTH}
              showCharacterLimit
              onChange={event =>
                onChangeFieldValue(event, 'angelInvestorDesignation')
              }
            />
            <RhfTextField
              name="angelInvestorURL"
              label={translate('company.addAngelInvestor.url')}
              placeholder={translate('company.placeholders.angelUrl')}
              control={control}
              errors={errors}
              wrapperClass="mb-5 w-full"
              onChange={event => onChangeFieldValue(event, 'angelInvestorURL')}
              showCharacterLimit
            />
            <RhfTextField
              name="angelInvestorDescription"
              label={translate('company.addAngelInvestor.description')}
              placeholder={translate('company.addAngelInvestor.description')}
              control={control}
              multiline={true}
              errors={errors}
              maxLength={LONG_FIELD_MAX_LENGTH}
              rows={3}
              wrapperClass="mb-5 w-full"
              onChange={event =>
                onChangeFieldValue(event, 'angelInvestorDescription')
              }
              showCharacterLimit
            />
          </>
        );

      case 1:
        return (
          <>
            <div className="flex flex-row items-center pb-2 text-base font-normal text-black/60">
              {translate('company.addAngelInvestor.logoDescription')}
            </div>
            <div className="flex flex-row items-center pb-2 text-base font-normal text-black/60">
              {translate('company.addAngelInvestor.logo')}
            </div>
            {showAngelInvestorLogoFileUploader ? (
              <ImageUploader
                name="angelInvestorLogo"
                enableCrop={true}
                sizeInMb={3}
                supportedFormats={IMAGE_FORMATS}
                fileUploadSuccessHandler={onFileUploadSuccess}
                deleteFileHandler={() => deleteFile('angelInvestorLogo')}
                errors={errors}
                previousFile={getValues('angelInvestorLogo.file')}
                aspectRatio={1}
                showPreview={true}
                customUploadText={translate('company.uploadImageText', {
                  aspectRatio: '1:1'
                })}
              />
            ) : (
              <ImagePreviewCard
                name="angelInvestorLogo"
                isEdit={true}
                handleDeleteClick={() => deleteFile('angelInvestorLogo')}
                imageUrl={getImageUrl()}
                fileName={getFileName()}
              />
            )}
          </>
        );
      default:
        return <></>;
    }
  };

  const onDeleteInvestorClick = () => {
    setModalOpen(false);
    setIsDeleteModalOpen(true);
  };

  useEffect(() => {
    if (memberDetails?.id) {
      reset({
        angelInvestorName: memberDetails.name,
        angelInvestorURL: memberDetails.url || null,
        angelInvestorDesignation: memberDetails.designation || null,
        angelInvestorLogo: {
          file: memberDetails.logoPath
            ? new File([], getFileNameFromUrl(memberDetails.logoPath))
            : undefined,
          path: memberDetails.logoPath
        },
        angelInvestorDescription: memberDetails.description ?? '',
        angelInvestorOrder: memberDetails.order ?? 0
      });
    }
  }, [memberDetails, isOpen]);

  useEffect(() => {
    if (disableChooseInvestor) setActiveStep(0);
    else setActiveStep(-1);
    setDeleteClickedFile('');
    reset({});
  }, [isOpen]);

  return (
    <Modal open={isOpen} onClose={handleCloseModal}>
      <div className="flex flex-col justify-center items-center h-full bg-black/60">
        <Paper
          style={{
            paddingTop: 20,
            paddingBottom: 20,
            width: 450,
            maxHeight: 700,
            overflow: 'auto'
          }}>
          <div className="flex flex-col bg-white">
            <div className="px-5 pb-5 mb-5 text-lg font-medium leading-6 border-b border-gray-200 ">
              {activeStep === -1
                ? translate('company.addAngelInvestor.chooseInvestor')
                : `${memberDetails?.id ? 'Edit' : 'Add'} ${translate(
                    'company.addAngelInvestor.title'
                  )}`}
            </div>
            {activeStep !== -1 && (
              <div className="px-5">
                <HorizontalStepper
                  activeStep={activeStep}
                  handleStep={handleStepClick}
                  steps={steps}
                />
              </div>
            )}
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="p-5 w-full min-h-[250px]">{renderForm()}</div>
              <div className="flex justify-between px-5 pt-5 w-full text-lg font-medium leading-6 border-t border-gray-200">
                <div>
                  {memberDetails?.id && (
                    <Button
                      onClick={onDeleteInvestorClick}
                      variant="text"
                      sx={{ color: colors.warningRed, minWidth: '125pxs' }}>
                      {translate('company.investorsTable.deleteInvestor')}
                    </Button>
                  )}
                </div>
                <DualButton
                  primaryButtonLabel={translate(
                    activeStep == steps.length - 1
                      ? 'company.addAngelInvestor.save'
                      : 'company.addAngelInvestor.next'
                  )}
                  secondaryButtonVariant="text"
                  secondaryButtonLabel={translate(
                    activeStep == steps.length - 1
                      ? 'company.addAngelInvestor.back'
                      : 'company.addAngelInvestor.cancel'
                  )}
                  secondaryButtonCustomStyle={{
                    color: colors.primaryBlue
                  }}
                  onChangePrimaryButton={onClickPrimaryButton}
                  onChangeSecondaryButton={onClickSecondaryButton}
                  isPrimaryButtonLoading={isLoading || isInvestorUpdating}
                />
              </div>
            </form>
          </div>
        </Paper>
      </div>
    </Modal>
  );
};

export default AddAngelInvestorModal;
